/* storybook-check-ignore */
import { Button, Icon } from '@opendoor/bricks/core';
import ReserveATourSlotContent, { ReserveATourSlotContentProps } from './ReserveATourSlotContent';
import { useState } from 'react';
import { Listing } from 'declarations/exclusives/listing';
import ReserveATourSelectedSpotConfirmationModal from './ReserveATourSelectedSpotConfirmationModal';
import { useRequestATour } from './useRequestATour';

export type Props = ReserveATourSlotContentProps & {
  onClick: (props: {
    slotContentProps?: ReserveATourSlotContentProps;
    contextualData: { buttonText: string; origin: string };
  }) => void;
};

export default function ReserveATourSlotButton({
  onClick,
  dashedBorder,
  listing,
  reportEvent,
  spotReserved,
  ...props
}: Props & {
  dashedBorder?: boolean;
  listing: Listing;
  reportEvent: ReturnType<typeof useRequestATour>['reportEvent'];
  spotReserved?: boolean;
}) {
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const buttonText = props.slot
    ? props.slotExists
      ? 'Reserve tour slot'
      : 'Request tour slot'
    : 'Request new time';
  const onButtonClick = () => {
    if (props.slot) {
      if (props.slotExists) {
        reportEvent('tour-request', {
          buttonText,
          slot: props.slot,
          origin: 'request-a-tour-buttons',
        });
        setConfirmationModalIsOpen(true);
      } else {
        onClick({
          slotContentProps: props,
          contextualData: { buttonText, origin: 'request-a-tour-buttons' },
        });
      }
    } else {
      onClick({ contextualData: { buttonText, origin: 'request-a-tour-buttons' } });
    }
  };
  const isPrimaryButton = (props.slot && props.slotExists) || (!props.slot && props.isPrimary);
  return (
    <>
      <Button
        border={dashedBorder ? '1.5px dashed' : '1px solid'}
        borderColor="neutrals40"
        backgroundColor="neutrals0"
        // @ts-expect-error TODO: @exclusives to resolve
        borderRadius="roundedSquare"
        height="4.5rem"
        padding="0.75rem 1rem 0.625rem 1rem"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-start"
        onClick={onButtonClick}
        disabled={spotReserved}
        _hover={{
          opacity: 0.9,
          backgroundColor: 'neutrals30',
          _disabled: {
            backgroundColor: 'neutrals20',
          },
        }}
        _disabled={{
          backgroundColor: 'neutrals20',
          cursor: 'not-allowed',
        }}
      >
        <ReserveATourSlotContent {...props} listing={listing} />
        <Button
          as="div"
          display={['none', 'none', 'flex']}
          aria-label={null}
          analyticsName=""
          onClick={(ev) => {
            ev.stopPropagation();
            onButtonClick();
          }}
          variant={isPrimaryButton ? 'primary' : 'secondary'}
          backgroundColor={isPrimaryButton ? undefined : 'transparent'}
          minHeight="none"
          maxWidth="162px"
          paddingX="0.75rem"
          fontSize="s0"
          lineHeight="s00"
          fontWeight="semibold"
          mt="0.25rem"
          mb="0.375rem"
          disabled={spotReserved}
        >
          {props.slot
            ? props.slotExists
              ? spotReserved
                ? 'Spot reserved'
                : 'Reserve tour slot'
              : 'Request tour slot'
            : 'Request new time'}
        </Button>
        <Icon
          name="chevron-right"
          marginY="auto"
          size={16}
          mr="0.25rem"
          display={['flex', 'flex', 'none']}
        />
      </Button>
      {confirmationModalIsOpen && props.slot && (
        <ReserveATourSelectedSpotConfirmationModal
          onClose={() => {
            setConfirmationModalIsOpen(false);
            reportEvent('tour-request', {
              buttonText: 'cancel',
              origin: 'reserve-a-tour-selected-spot-confirmation-modal',
              slot: props.slot,
            });
          }}
          onConfirm={() => {
            setConfirmationModalIsOpen(false);
            onClick({
              slotContentProps: props,
              contextualData: {
                buttonText: 'yes',
                origin: 'reserve-a-tour-selected-spots-confirmation-modal',
              },
            });
          }}
          listing={listing}
          slot={props.slot}
        />
      )}
    </>
  );
}
